import { useAuth } from '@practium/features/auth';
import { usePermissions } from '@practium/features/permissions';
import type {
	DefensiveFormData,
	FertilizerFormData,
	ManufacturerFormData,
	ProductsTab,
} from '@practium/features/products';
import { AddProductPage } from '@practium/features/products';
import { ProductModel } from '@practium/features/products/components/3D/ProductModel';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
	useGetDefensiveById,
	useGetFertilizerById,
	useGetManufacturerById,
} from '@practium/features/api';

const normalizeApplicationForm = (value?: string | string[]) => {
	if (Array.isArray(value)) return value;
	return value ? [value] : [];
};

export const ViewProductPageClient = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { user } = useAuth();
	const { abilities } = usePermissions();
	const canUpdateProducts = abilities.can('update', 'products');
	const canDeleteProducts = abilities.can('delete', 'products');

	const type = (searchParams.get('type') as ProductsTab) || 'defensives';
	const id = searchParams.get('id') || '';

	const isDefensive = type === 'defensives';
	const isFertilizer = type === 'fertilizers';
	const isManufacturer = type === 'manufacturers' || (!isDefensive && !isFertilizer);

	const isAdminOrMaster = user?.role === 'admin';
	const currentUserId = user?._id || user?._id;

	const [productName, setProductName] = useState('');
	const [productNature, setProductNature] = useState('');
	const [productManufacturer, setProductManufacturer] = useState('');
	const [productExtra, setProductExtra] = useState('');

	const { data: defensive, isLoading: isLoadingDef } = useGetDefensiveById(id, isDefensive && !!id);
	const { data: fertilizer, isLoading: isLoadingFert } = useGetFertilizerById(
		id,
		isFertilizer && !!id
	);
	const { data: manufacturer, isLoading: isLoadingMan } = useGetManufacturerById(
		id,
		isManufacturer && !!id
	);

	const isLoading =
		(isDefensive && isLoadingDef) ||
		(isFertilizer && isLoadingFert) ||
		(isManufacturer && isLoadingMan);

	const { initialData, isOwner, initial3DProps } = useMemo(() => {
		if (isDefensive && defensive) {
			const initialData: DefensiveFormData = {
				commercialName: defensive.commercialName ?? '',
				manufacturerId: defensive.manufacturer?._id ?? '',
				nature: defensive.nature ?? '',
				activeIngredients: defensive.activeIngredients?.map((i: { name: string; concentration: string; }) => ({
					name: i.name,
					concentration: String(i.concentration),
				})) ?? [{ name: '', concentration: '' }],
				class: Array.isArray(defensive.class) ? defensive.class : defensive.class ? [defensive.class] : [],
				culture: defensive.culture ?? [],
				applicationForm: normalizeApplicationForm(defensive.applicationForm),
				dose: defensive.dose != null ? String(defensive.dose) : '',
				doseMeasurementUnit: defensive.doseMeasurementUnit ?? 'L/ha',
				syrupVolume: defensive.syrupVolume != null ? String(defensive.syrupVolume) : '',
				commonPests: defensive.commonPests?.[0] ?? '',
			};
			return {
				isOwner: defensive.userId === currentUserId || isAdminOrMaster,
				initialData,
				initial3DProps: {
					name: initialData.commercialName,
					nature: initialData.nature,
					manufacturer: defensive.manufacturer?.tradingName ?? defensive.manufacturer?.businessName ?? '',
					extra: initialData.class.join(', '),
				}
			};
		}

		if (isFertilizer && fertilizer) {
			const initialData: FertilizerFormData = {
				name: fertilizer.name,
				manufacturerId: fertilizer.manufacturer?._id ?? '',
				category: fertilizer.category,
				applicationMethods:
					fertilizer.applicationMethods ??
					(fertilizer.applicationMethod ? [fertilizer.applicationMethod] : []),
				nature: fertilizer.nature,
				active: fertilizer.active ?? fertilizer.isPublic,
				note: fertilizer.note ?? '',
				N: fertilizer.N != null ? String(fertilizer.N) : '',
				P2O5: fertilizer.P2O5 != null ? String(fertilizer.P2O5) : '',
				K2O: fertilizer.K2O != null ? String(fertilizer.K2O) : '',
				Ca: fertilizer.Ca != null ? String(fertilizer.Ca) : '',
				Mg: fertilizer.Mg != null ? String(fertilizer.Mg) : '',
				S: fertilizer.S != null ? String(fertilizer.S) : '',
				C: fertilizer.C != null ? String(fertilizer.C) : '',
				B: fertilizer.B != null ? String(fertilizer.B) : '',
				Cu: fertilizer.Cu != null ? String(fertilizer.Cu) : '',
				Fe: fertilizer.Fe != null ? String(fertilizer.Fe) : '',
				Mn: fertilizer.Mn != null ? String(fertilizer.Mn) : '',
				Zn: fertilizer.Zn != null ? String(fertilizer.Zn) : '',
				Mo: fertilizer.Mo != null ? String(fertilizer.Mo) : '',
				CaO: fertilizer.CaO != null ? String(fertilizer.CaO) : '',
				MgO: fertilizer.MgO != null ? String(fertilizer.MgO) : '',
				PRNT: fertilizer.PRNT != null ? String(fertilizer.PRNT) : '',
				solubleSaltFraction:
					fertilizer.solubleSaltFraction != null ? String(fertilizer.solubleSaltFraction) : '',
				avatars: fertilizer.avatars,
			};
			return {
				isOwner: fertilizer.owner === currentUserId || isAdminOrMaster,
				initialData,
				initial3DProps: {
					name: initialData.name,
					nature: initialData.nature,
					manufacturer:
						fertilizer.manufacturer?.tradingName ?? fertilizer.manufacturer?.businessName ?? '',
					extra: initialData.category,
				},
			};
		}

		if (isManufacturer && manufacturer) {
			const initialData: ManufacturerFormData = {
				tradingName: manufacturer.tradingName ?? '',
				businessName: manufacturer.businessName ?? '',
				cnpj: manufacturer.cnpj ?? '',
			};
			return {
				isOwner: (manufacturer.userId || manufacturer.user) === currentUserId || isAdminOrMaster,
				initialData,
				initial3DProps: {
					name: manufacturer.tradingName ?? manufacturer.businessName ?? '',
					nature: '',
					manufacturer: '',
					extra: '',
				},
			};
		}

		return { initialData: null, isOwner: false, initial3DProps: null };
	}, [
		isDefensive,
		defensive,
		isFertilizer,
		fertilizer,
		isManufacturer,
		manufacturer,
		currentUserId,
		isAdminOrMaster,
	]);

	useEffect(() => {
		if (initial3DProps) {
			setProductName(initial3DProps.name);
			setProductNature(initial3DProps.nature);
			setProductManufacturer(initial3DProps.manufacturer);
			setProductExtra(initial3DProps.extra);
		}
	}, [initial3DProps]);

	if (isLoading || !initialData) return null;

	return (
		<AddProductPage
			type={type}
			onBack={() => navigate('/produtos')}
			initialMode="view"
			initialData={initialData}
			canEdit={isOwner && canUpdateProducts}
			onDelete={isOwner && canDeleteProducts ? () => navigate('/produtos') : undefined}
			onIdentifierChange={(name, nature, manufacturer, extra) => {
				setProductName(name);
				setProductNature(nature);
				setProductManufacturer(manufacturer);
				setProductExtra(extra);
			}}
			preview3DSlot={
				<ProductModel
					type={type}
					name={productName}
					nature={productNature}
					manufacturer={productManufacturer}
					extra={productExtra}
				/>
			}
			id={id}
		/>
	);
};
